import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import PropTypes from "prop-types";
import React, { lazy, Suspense, useState } from "react";
import Button from "web/react/components/button/button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { usePrevious } from "web/react/hooks/use-previous/use-previous";
import styles from "./save-for-later.css";

const basePropTypes = {
    className: PropTypes.string,
    isSaved: PropTypes.bool.isRequired,
    isSaving: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    cta: PropTypes.string,
    showExpressCheckout: PropTypes.bool,
    bingTraffic: PropTypes.bool,
};

const HeartAnimation = lazy(() =>
    import("web/react/components/lottie-animations/heart-animation/heart-animation")
);

export function SaveForLaterIcon({ onClick, isSaved, className, isSaving }) {
    const [isAnimating, setIsAnimating] = useState(false);
    function loadAnimation() {
        // Dynamically import heart animation when user hovers...
        import("web/react/components/lottie-animations/heart-animation/heart-animation");
    }

    const previousIsSaved = usePrevious(isSaving);
    const showAnimation = (!previousIsSaved && isSaving) || isAnimating;

    return (
        <button
            onClick={onClick}
            className={classnames(styles.icon, className, {
                [styles.iconSaved]: isSaved,
            })}
            onMouseEnter={loadAnimation}
        >
            {!showAnimation && (
                <SVGIcon
                    name={isSaved ? "heart-pressed" : "heart-unpressed"}
                    className={styles.svgIcon}
                />
            )}
            {showAnimation && (
                <Suspense fallback={<LoadingSpinner />}>
                    <div className={styles.animatedHeart}>
                        <HeartAnimation
                            onComplete={() => {
                                setIsAnimating(false);
                            }}
                            onAnimationStart={() => {
                                setIsAnimating(true);
                            }}
                        />
                    </div>
                </Suspense>
            )}
        </button>
    );
}

SaveForLaterIcon.propTypes = basePropTypes;

function getCTA(isSaved) {
    if (isSaved) {
        return gettext("in_stock_product.saved");
    } else {
        return gettext("save_for_later.cta.variant_b");
    }
}

export function SaveForLaterNormal({
    onClick,
    isSaved,
    isSaving,
    className,
    cta,
    showExpressCheckout,
    bingTraffic,
}) {
    let buttonStyle = "secondary";
    if (showExpressCheckout && bingTraffic === false) {
        buttonStyle = "cta";
    }

    return (
        <Button
            styleType={isSaved ? "temp-grey" : buttonStyle}
            onClick={onClick}
            className={className}
            fullWidth
            disabled={isSaving}
        >
            <SVGIcon className={styles.svg} name={isSaved ? "heart" : "heart-empty"} />{" "}
            <span>{cta ? cta : getCTA(isSaved)}</span>
        </Button>
    );
}
SaveForLaterNormal.propTypes = basePropTypes;

export function SaveForLaterStockAlert({ onClick, isSaved, isSaving, className, cta }) {
    let ctaText;
    if (isSaved) {
        ctaText = getCTA(isSaved);
    } else {
        ctaText = cta ? cta : getCTA(isSaved);
    }
    return (
        <Button
            styleType={isSaved ? "temp-grey" : "cta"}
            onClick={onClick}
            className={className}
            disabled={isSaving}
        >
            <SVGIcon className={styles.svg} name={"bell"} /> <span>{ctaText}</span>
        </Button>
    );
}

SaveForLaterStockAlert.propTypes = basePropTypes;

const BUTTON_TYPES = {
    icon: SaveForLaterIcon,
    normal: SaveForLaterNormal,
    stockAlert: SaveForLaterStockAlert,
};

function SaveForLater({ type, ...props }) {
    let Component = BUTTON_TYPES[type];
    if (!Component) {
        throw new Error(`Invalid button type ${type}!`);
    }

    return <Component {...props} />;
}

export default SaveForLater;

SaveForLater.propTypes = {
    ...basePropTypes,
    type: PropTypes.oneOf(["icon", "normal", "stockAlert"]).isRequired,
};

SaveForLater.defaultProps = {
    isSaving: false,
};
